import React, { useState, useEffect } from "react";
import { Box, Stack, Typography, Grid, CircularProgress, Tooltip } from "@mui/material";
import {
  Link2,
  Copy,
  RefreshCw,
  Check,
  UserPlus,
  User,
  Rotate3D,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent } from "../../components/Card";
import { Button } from "../../components/Button";
import { CustomTextField } from "../../components/CustomTextField";
import { Separator } from "../../components/Separator";
import { MetricCard } from "../../components/MetricCard";
import { useAuth } from "../../components/contexts/AuthContext";
import { useTheme } from "../../theme/useTheme";
import { getData } from "../../components/hooks/getData";
import apiClient from "../../api-client";
import { refreshWelcomeProgress } from "../../Tutorials/welcomeProgress";

export function InvitationLinkSection({
  clientId,
  onSuccess,
  onError,
  refreshKey,
  clientDetailData,
  clientDetailLoading,
  clientDetailError,
}) {
  const { interceptorReady } = useAuth();
  const { theme, colors } = useTheme();
  const compactMetricCardSx = {
    "& .MuiPaper-root > .MuiBox-root": {
      padding: "12px",
    },
    "& .MuiTypography-h4": {
      fontSize: "1.375rem",
      lineHeight: 1.2,
    },
    "& .MuiTypography-body2": {
      fontSize: "0.75rem",
    },
    "& .MuiPaper-root svg": {
      width: 18,
      height: 18,
    },
  };

  const [invitationLink, setInvitationLink] = useState("");
  const [copiedLink, setCopiedLink] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [error, setError] = useState("");
  const [pendingInvitesCount, setPendingInvitesCount] = useState(0);
  const [refreshClientDetail, setRefreshClientDetail] = useState(0);

  // Cargar datos del cliente
  const hasParentClientDetail =
    clientDetailData !== undefined ||
    clientDetailLoading !== undefined ||
    clientDetailError !== undefined;

  const shouldFetchClientDetail =
    !hasParentClientDetail && !!clientId && interceptorReady;

  const {
    data: fetchedClientDetailData,
    error: fetchedClientDetailError,
  } = getData(
    shouldFetchClientDetail ? `clients/${clientId}/` : null,
    shouldFetchClientDetail,
    [clientId, refreshClientDetail, refreshKey]
  );

  const resolvedClientDetailData = hasParentClientDetail
    ? clientDetailData
    : fetchedClientDetailData;
  const resolvedClientDetailError = hasParentClientDetail
    ? clientDetailError
    : fetchedClientDetailError;

  const loadingClientDetail = hasParentClientDetail
    ? !!clientId && clientDetailLoading && !clientDetailError
    : !!clientId && !resolvedClientDetailData && !resolvedClientDetailError;

  // Sincronizar datos cuando se cargan
  useEffect(() => {
    if (resolvedClientDetailData) {
      setPendingInvitesCount(
        resolvedClientDetailData.client_invites_active?.length || 0
      );
    }
  }, [resolvedClientDetailData]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(invitationLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error("Error copiando al portapapeles:", err);
    }
  };

  const generateInvitationLink = async () => {
    setError("");
    setIsGeneratingLink(true);

    try {
      if (!clientId) {
        setError("No hay cliente disponible");
        setIsGeneratingLink(false);
        return;
      }

      const response = await apiClient.post(
        `clients/${clientId}/generate_client_invites/`
      );

      if (response.data && response.data.url) {
        setInvitationLink(response.data.url);
        setPendingInvitesCount((prev) => prev + 1);
        refreshWelcomeProgress();
        if (onSuccess) onSuccess("Enlace de invitación generado exitosamente");
      }
    } catch (err) {
      console.error("Error generando enlace:", err);
      if (err.response && err.response.status === 403) {
        const errorMsg = "No tiene permiso para esta acción";
        setError(errorMsg);
        if (onError) onError(errorMsg);
      } else {
        const errorMsg =
          err.response?.data?.message ||
          "Error al generar el enlace de invitación";
        setError(errorMsg);
        if (onError) onError(errorMsg);
      }
      setRefreshClientDetail((prev) => prev + 1);
    } finally {
      setIsGeneratingLink(false);
    }
  };

  if (loadingClientDetail) {
    return (
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            padding: 6,
            textAlign: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            Cargando datos del cliente...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const invitationContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, flex: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "1.125rem",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Link2 size={18} />
          Enlace de invitación
        </Typography>
      </Box>

      {/* Mostrar error si existe */}
      {error && (
        <Box
          sx={{
            backgroundColor: `${colors.error.light}22`,
            padding: 2,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            gap: 1,
            border: `1px solid ${colors.error.light}`,
          }}
        >
          <AlertCircle size={16} color={colors.error.main} />
          <Typography
            variant="body2"
            sx={{
              color: colors.error.dark,
              fontSize: "0.875rem",
            }}
          >
            {error}
          </Typography>
        </Box>
      )}

      {/* Enlace principal */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            Tu enlace de invitación
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          sx={{ minWidth: 0, "& > :first-child": { flex: 1, minWidth: 0 } }}
        >
          <CustomTextField
            value={invitationLink}
            readOnly
            placeholder="Pulsa el botón de generar para crear un enlace"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.875rem",
            }}
          />
          <Tooltip title="Generar enlace" arrow>
            <span>
              <Button
                variant="outline"
                onClick={generateInvitationLink}
                disabled={isGeneratingLink}
              >
                {isGeneratingLink ? (
                  <RefreshCw
                    size={16}
                    style={{
                      animation: "spin 1s linear infinite",
                      transformOrigin: "center",
                    }}
                  />
                ) : (
                  <Rotate3D size={16} />
                )}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Copiar enlace" arrow>
            <span>
              <Button variant="outline" onClick={copyToClipboard}>
                {copiedLink ? <Check size={16} /> : <Copy size={16} />}
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {copiedLink && (
          <Typography
            variant="body2"
            sx={{
              color: colors.success.main,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Check size={12} />
            ¡Enlace copiado al portapapeles!
          </Typography>
        )}
      </Box>

      <Separator />

      {/* Información sobre el enlace */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Estadísticas del enlace
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <MetricCard
              icon={UserPlus}
              iconColor={colors.info.main}
              iconBackgroundColor={`${colors.info.light}22`}
              value={
                resolvedClientDetailData
                  ? resolvedClientDetailData.client_users_count
                  : 0
              }
              label="Completados"
              sx={compactMetricCardSx}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <MetricCard
              icon={User}
              iconColor={colors.warning.main}
              iconBackgroundColor={`${colors.warning.light}22`}
              value={pendingInvitesCount}
              label="Pendientes"
              sx={compactMetricCardSx}
            />
          </Grid>
        </Grid>
      </Box>

      <Separator />

      {/* Información adicional */}
      <Box
        sx={{
          backgroundColor: `${colors.info.light}22`,
          padding: 2,
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 500,
            color: colors.info.dark,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Link2 size={16} />
          Cómo funciona
        </Typography>
        <Box
          component="ul"
          sx={{
            margin: 0,
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {[
            "Copia el enlace de invitación",
            "Envíalo a tus clientes por email, WhatsApp o SMS",
            "El cliente se registra y se vincula automáticamente contigo",
            "Cada link es único para cada cliente",
            "Puedes invitar a más clientes con un nuevo enlace en cualquier momento",
          ].map((step, index) => (
            <Box
              key={index}
              component="li"
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                fontSize: "0.875rem",
                color: colors.info.dark,
              }}
            >
              <Typography
                component="span"
                sx={{
                  color: colors.info.main,
                  fontWeight: 600,
                  minWidth: "16px",
                }}
              >
                {index + 1}.
              </Typography>
              <Typography component="span" sx={{ fontSize: "0.875rem" }}>
                {step}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          flex: 1,
          padding: { xs: 2, sm: 3 },
        }}
      >
        {invitationContent}
      </CardContent>
    </Card>
  );
}
